import { useState } from 'react';
import type { AuditEntryInfo } from '@shared/types';
import { formatDateTime } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, PageHeader } from '../components/ui';
import { Field, Input, Notice, Select } from '../components/form';
import { Modal, useToast } from '../components/feedback';
import { Table, Pagination, type Column } from '../components/table';

interface AuditResponse {
  items: AuditEntryInfo[];
  page: number;
  pageSize: number;
  total: number;
}

const RESULT_META = {
  success: { label: '成功', tone: 'ok' as const },
  denied: { label: '被拒', tone: 'warn' as const },
  failure: { label: '失败', tone: 'danger' as const },
};

/**
 * 操作记录（审计日志）。
 *
 * 作用域是当前组织；管理员只能看到本组织的记录。
 * 清理只支持「某个时间点之前」，不提供「清空全部」—— 那种按钮误点一次就无法追责。
 */
export function AuditPage() {
  const can = useAuthStore((state) => state.can);
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [result, setResult] = useState('');
  const [pruneOpen, setPruneOpen] = useState(false);
  const [before, setBefore] = useState('');

  const query = new URLSearchParams({ page: String(page), pageSize: '20' });
  if (action.trim() !== '') query.set('action', action.trim());
  if (actor.trim() !== '') query.set('actor', actor.trim());
  if (result !== '') query.set('result', result);

  const audit = useAsync<AuditResponse>(() => api.get(`/api/audit?${query.toString()}`), [
    page,
    action,
    actor,
    result,
  ]);

  const columns: Column<AuditEntryInfo>[] = [
    {
      key: 'at',
      header: '时间',
      render: (row) => <span className="tabular text-fg-muted">{formatDateTime(row.at)}</span>,
    },
    {
      key: 'actor',
      header: '操作人',
      render: (row) => <span className="truncate text-fg">{row.actorEmail}</span>,
    },
    {
      key: 'action',
      header: '动作',
      render: (row) => <span className="font-mono text-xs text-fg">{row.action}</span>,
    },
    {
      key: 'target',
      header: '对象',
      render: (row) => (
        <span className="text-fg-muted">
          {row.targetType}
          {row.targetId ? ` · ${row.targetId}` : ''}
        </span>
      ),
    },
    {
      key: 'detail',
      header: '说明',
      render: (row) => <span className="text-fg-muted">{row.detail ?? '—'}</span>,
    },
    {
      key: 'result',
      header: '结果',
      render: (row) => {
        const meta = RESULT_META[row.result];
        return <Badge tone={meta.tone}>{meta.label}</Badge>;
      },
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="操作记录"
        description="成员管理、接入配置、安全设置等动作都会留痕。记录写入失败会直接让操作失败，因此不会出现「做了但没记」。"
        action={
          can('audit.prune') ? (
            <Button icon="trash" onClick={() => setPruneOpen(true)}>
              清理历史
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader title="筛选" />
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="动作" htmlFor="filter-action" hint="支持部分匹配，例如 member">
            <Input
              id="filter-action"
              value={action}
              onChange={(value) => {
                setAction(value);
                setPage(1);
              }}
              placeholder="member.update"
            />
          </Field>
          <Field label="操作人" htmlFor="filter-actor">
            <Input
              id="filter-actor"
              value={actor}
              onChange={(value) => {
                setActor(value);
                setPage(1);
              }}
              placeholder="someone@example.com"
            />
          </Field>
          <Field label="结果">
            <Select
              value={result}
              onChange={(value) => {
                setResult(value);
                setPage(1);
              }}
              options={[
                { value: '', label: '全部' },
                { value: 'success', label: '成功' },
                { value: 'denied', label: '被拒' },
                { value: 'failure', label: '失败' },
              ]}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="记录"
          action={<Button size="sm" icon="refresh" onClick={() => void audit.reload()}>刷新</Button>}
        />
        <Table
          caption="操作记录"
          columns={columns}
          rows={audit.data?.items ?? []}
          getKey={(row) => row.id}
          loading={audit.loading}
          empty={{ title: '没有匹配的记录', description: '换个筛选条件试试。' }}
        />
        <Pagination
          page={page}
          pageSize={audit.data?.pageSize ?? 20}
          total={audit.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={pruneOpen}
        title="清理历史记录"
        description="只删除指定时间之前的记录，更近的记录不会受影响。"
        onClose={() => setPruneOpen(false)}
        footer={
          <>
            <Button onClick={() => setPruneOpen(false)}>取消</Button>
            <Button
              variant="danger"
              onClick={async () => {
                try {
                  const result = await api.post<{ deleted: number }>('/api/audit/prune', { before });
                  setPruneOpen(false);
                  toast.push(`已清理 ${result.deleted} 条记录`, 'ok');
                  await audit.reload();
                } catch (error) {
                  toast.push(describeError(error), 'danger');
                }
              }}
            >
              确认清理
            </Button>
          </>
        }
      >
        <Field label="清理该时间之前的记录" htmlFor="prune-before" required hint="格式：2026-01-01">
          <Input id="prune-before" value={before} onChange={setBefore} placeholder="2026-01-01" />
        </Field>
        <div className="mt-3">
          <Notice tone="warn">这次清理本身也会被记录，因此它一定留在记录里。</Notice>
        </div>
      </Modal>
    </div>
  );
}
